/**
 * smoothModelOffThread — drop-in replacement for MeshOps.smoothModel that runs
 * the crease-normal pass in a Web Worker (see workers/smooth_worker.ts for the
 * profile evidence). Mirrors smoothModel's skip rules exactly (userData.noSmooth,
 * alpha/transparent/transmissive materials) and falls back to the synchronous
 * engine path on any worker failure, so visuals can never regress.
 *
 * Serialization copies each attribute into a standalone denormalized Float32
 * array (via getComponent — handles InterleavedBufferAttribute too) so the
 * LIVE geometry the renderer holds is never detached by the transfer.
 */
import * as THREE from 'three/webgpu';
import { MeshOps } from 'enginex/assets/MeshOps.js';
import type { SmoothReply, SmoothAttr } from '../workers/smooth_worker.js';

let worker: Worker | null = null;
let workerBroken = false;
let nextId = 1;
const pending = new Map<number, { resolve: (r: SmoothReply) => void }>();

/**
 * Stall deadline: a worker wedged mid-import (cold dev-server dep-optimize
 * race) never fires 'error', so without a timeout the await in smoothOne hangs
 * forever — and every GLB load queued behind it stalls with it. Measured as
 * QUIET time (no reply from ANY request), not per-request latency: a burst of
 * queued meshes serializes in the worker, so a healthy-but-busy worker answers
 * something every few seconds while a wedged one never answers at all.
 */
const SMOOTH_QUIET_LIMIT_MS = 20_000;
/** Before the FIRST-ever reply the worker may still be importing its module
 *  graph (cold vite dep-optimize over LAN TLS on a Quest takes tens of
 *  seconds) — the chunk/water workers' boot-vs-stall split. A 20 s boot
 *  budget false-killed a healthy cold worker and pinned the whole session to
 *  the main-thread smoothing fallback. */
const SMOOTH_BOOT_LIMIT_MS = 60_000;
let lastBeatMs = 0;

function failSmoothWorker(error: string): void {
  workerBroken = true;
  worker?.terminate();
  worker = null;
  for (const [, p] of pending) p.resolve({ id: -1, error });
  pending.clear();
}

function getWorker(): Worker | null {
  if (workerBroken) return null;
  if (!worker) {
    try {
      worker = new Worker(new URL('../workers/smooth_worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (ev: MessageEvent<SmoothReply>) => {
        lastBeatMs = performance.now();
        const p = pending.get(ev.data.id);
        if (p) { pending.delete(ev.data.id); p.resolve(ev.data); }
      };
      worker.onerror = () => failSmoothWorker('worker crashed');
    } catch { workerBroken = true; return null; }
  }
  return worker;
}

function isAlphaMaterial(material: THREE.Material | THREE.Material[]): boolean {
  const mats = Array.isArray(material) ? material : [material];
  return mats.some((m) => {
    const mm = m as unknown as { alphaTest?: number; transparent?: boolean; transmission?: number };
    return (mm.alphaTest ?? 0) > 0 || mm.transparent === true || (mm.transmission ?? 0) > 0;
  });
}

/** Copy an attribute to a standalone Float32Array (denormalizes, deinterleaves). */
function copyAttr(name: string, attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): SmoothAttr {
  const { count, itemSize } = attr;
  const array = new Float32Array(count * itemSize);
  for (let i = 0; i < count; i++) {
    for (let c = 0; c < itemSize; c++) array[i * itemSize + c] = attr.getComponent(i, c);
  }
  return { name, array, itemSize };
}

async function smoothOne(mesh: THREE.Mesh, creaseAngleDeg: number): Promise<boolean> {
  const w = getWorker();
  if (!w) return false;
  const geo = mesh.geometry;
  const attributes: SmoothAttr[] = [];
  const transfers: ArrayBuffer[] = [];
  for (const [name, attr] of Object.entries(geo.attributes)) {
    const a = copyAttr(name, attr as THREE.BufferAttribute);
    attributes.push(a);
    transfers.push(a.array.buffer as ArrayBuffer);
  }
  let index: Uint32Array | null = null;
  if (geo.index) {
    index = new Uint32Array(geo.index.count);
    for (let i = 0; i < geo.index.count; i++) index[i] = geo.index.getX(i);
    transfers.push(index.buffer as ArrayBuffer);
  }
  const id = nextId++;
  const reply = await new Promise<SmoothReply>((resolve) => {
    const sentAt = performance.now();
    let deadline = 0;
    const check = (): void => {
      if (!pending.has(id)) return; // already answered
      const limit = lastBeatMs === 0 ? SMOOTH_BOOT_LIMIT_MS : SMOOTH_QUIET_LIMIT_MS;
      const quiet = performance.now() - Math.max(lastBeatMs, sentAt);
      if (quiet < limit) { deadline = window.setTimeout(check, limit - quiet); return; }
      failSmoothWorker('worker stalled'); // resolves this promise via pending
    };
    deadline = window.setTimeout(check, SMOOTH_QUIET_LIMIT_MS);
    pending.set(id, { resolve: (r) => { clearTimeout(deadline); resolve(r); } });
    w.postMessage({ id, creaseAngleDeg, index, attributes }, transfers);
  });
  if (reply.error || !reply.attributes) return false;
  const next = new THREE.BufferGeometry();
  for (const a of reply.attributes) next.setAttribute(a.name, new THREE.BufferAttribute(a.array, a.itemSize));
  next.boundingBox = null;
  next.boundingSphere = null;
  const old = mesh.geometry;
  mesh.geometry = next;
  old.dispose();
  return true;
}

/** Worker-side smoothModel; per-mesh sequential so main-thread copies stay small. */
export async function smoothModelOffThread(root: THREE.Object3D, creaseAngleDeg = 50): Promise<void> {
  const targets: THREE.Mesh[] = [];
  root.traverse((o) => { if ((o as THREE.Mesh).isMesh) targets.push(o as THREE.Mesh); });
  for (const mesh of targets) {
    const noSmooth = (mesh.userData as { noSmooth?: boolean })?.noSmooth === true;
    if (noSmooth || isAlphaMaterial(mesh.material) || !mesh.geometry?.getAttribute('position')) continue;
    let ok = false;
    try { ok = await smoothOne(mesh, creaseAngleDeg); } catch { ok = false; }
    if (!ok) {
      // Parity fallback: the engine's synchronous path on this one mesh.
      try { await MeshOps.smoothModel(mesh, { creaseAngle: creaseAngleDeg }); } catch { /* skip */ }
    }
  }
}
